import { Controller } from 'stimulus';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import annotationPlugin from 'chartjs-plugin-annotation';
import {
  withDisconnectBeforeCache,
  withPreviewPrevention,
} from '../javascript/lib/stimulus_turbo';
import { t } from '../javascript/lib/i18n';

/**
 * Renders a Chart.js chart for AI message responses.
 * Supports line and bar charts for visualizing data trends.
 */
class AiMessageChartController extends Controller {
  static values = {
    type: { type: String, default: 'line' },
    data: Object,
    options: Object,
  };

  connect() {
    this.initChart();
  }

  disconnect() {
    this.destroyChart();
  }

  initChart() {
    if (!this.element) return;

    this.chart = new Chart(this.element, {
      type: this.typeValue,
      data: this.chartData,
      options: this.chartOptions,
      plugins: [ChartDataLabels, annotationPlugin],
    });
  }

  destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  get chartData() {
    const data = this.dataValue || {};

    // Transform datasets to extract labels from data points
    const datasets = data.datasets || [];

    if (datasets.length === 0) {
      return { labels: [], datasets: [] };
    }

    if (this.rawPointChart) {
      return {
        datasets: datasets.map((dataset) => ({
          ...dataset,
          data: dataset.data || [],
        })),
      };
    }

    // Extract unique x values for labels
    const allXValues = new Set();
    datasets.forEach((dataset) => {
      (dataset.data || []).forEach((point) => {
        if (point && point.x) {
          allXValues.add(point.x);
        }
      });
    });

    const labels = Array.from(allXValues).sort();

    return {
      labels,
      datasets: datasets.map((dataset) => ({
        ...dataset,
        // Transform data points from {x, y} to just y values aligned with labels
        data: labels.map((label) => {
          const point = (dataset.data || []).find((p) => p && p.x === label);
          return point ? point.y : null;
        }),
      })),
    };
  }

  get chartOptions() {
    const yFormat = this.detectYFormat();

    const INK = '#21413b';
    const INK_SOFT = '#5d6d68';
    const GRID = 'rgba(31, 65, 59, 0.08)';
    const FONT_FAMILY =
      "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      // Breathing room so axis labels and the bottom legend never clip.
      layout: {
        padding: { top: 8, right: 12, bottom: 4, left: 4 },
      },
      font: {
        family: FONT_FAMILY,
        size: 12,
      },
      color: INK_SOFT,
      interaction: {
        intersect: false,
        mode: 'index',
      },
      plugins: {
        legend: {
          display: this.shouldShowLegend,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            boxHeight: 8,
            padding: 16,
            color: INK,
            font: {
              family: FONT_FAMILY,
              size: 12,
              weight: '500',
            },
          },
        },
        title: {
          color: INK,
          padding: { bottom: 12 },
          font: {
            family: FONT_FAMILY,
            size: 14,
            weight: '700',
          },
        },
        datalabels: {
          display: this.optionsValue?.showDataLabels === true,
          anchor: this.radialChart ? 'center' : 'end',
          align: this.radialChart ? 'center' : 'top',
          offset: 2,
          color: this.radialChart ? '#ffffff' : INK,
          font: {
            family: FONT_FAMILY,
            weight: '700',
            size: 10,
          },
          formatter: (value, context) => {
            const parsed = this.dataLabelValue(value, context);
            return parsed === null || parsed === undefined ? '' : parsed;
          },
        },
        tooltip: {
          enabled: true,
          mode: 'index',
          intersect: false,
          usePointStyle: true,
          backgroundColor: 'rgba(33, 65, 59, 0.95)',
          padding: 10,
          cornerRadius: 8,
          titleColor: '#ffffff',
          bodyColor: '#e9efed',
          titleFont: { family: FONT_FAMILY, size: 12, weight: '700' },
          bodyFont: { family: FONT_FAMILY, size: 12 },
          boxPadding: 4,
          callbacks: {
            label: (context) => {
              if (this.radialChart) {
                return `${context.label}: ${context.formattedValue}`;
              }

              const value =
                this.rawPointChart && typeof context.raw === 'object'
                  ? context.raw.y ?? context.raw.r
                  : context.parsed[this.valueAxis];
              if (value === null || value === undefined) {
                return t('js.ai_message_chart.no_data_value', {
                  label: context.dataset.label,
                });
              }
              const formattedValue = this.formatValue(value, yFormat);
              return `${context.dataset.label}: ${formattedValue}`;
            },
          },
        },
      },
      // Category axis (labels) hides gridlines; value axis (numbers) shows them
      // and gets the duration/percentage tick formatting. These follow
      // indexAxis so horizontal bars get the value axis on x.
      scales: {
        [this.categoryAxis]: {
          border: { display: false },
          grid: {
            display: false,
          },
          ticks: {
            color: INK_SOFT,
            font: { family: FONT_FAMILY, size: 11 },
            maxRotation: 45,
            minRotation: 0,
            autoSkipPadding: 12,
          },
        },
        [this.valueAxis]: {
          beginAtZero: true,
          border: { display: false },
          grid: {
            color: GRID,
            drawTicks: false,
          },
          ticks: {
            color: INK_SOFT,
            font: { family: FONT_FAMILY, size: 11 },
            padding: 6,
            ...this.getYAxisTicks(yFormat),
          },
        },
      },
      elements: {
        line: {
          tension: 0.35,
          borderWidth: 2,
        },
        point: {
          radius: 3,
          hitRadius: 12,
          hoverRadius: 6,
        },
        bar: {
          borderRadius: 0,
          borderSkipped: false,
        },
        arc: {
          borderWidth: 2,
          borderColor: '#ffffff',
        },
      },
      animation: {
        duration: 300,
      },
    };

    if (this.radialChart) {
      delete baseOptions.scales;
    }

    // Merge with any provided options (excluding format as it's handled separately)
    const options = this.optionsValue || {};
    const mergedOptions = this.deepMerge(baseOptions, options);

    if (this.radialChart) {
      delete mergedOptions.scales;
    }

    // Ensure our tick callback isn't overwritten, while keeping tick styling.
    // Targets the value axis (x for horizontal bars, y otherwise).
    const valueAxis = this.valueAxis;
    if (yFormat && !this.radialChart && mergedOptions.scales?.[valueAxis]) {
      mergedOptions.scales[valueAxis].ticks = {
        ...mergedOptions.scales[valueAxis].ticks,
        ...this.getYAxisTicks(yFormat),
      };
    }

    return mergedOptions;
  }

  get rawPointChart() {
    return ['scatter', 'bubble'].includes(this.typeValue);
  }

  get radialChart() {
    return ['pie', 'doughnut', 'polarArea', 'radar'].includes(this.typeValue);
  }

  get shouldShowLegend() {
    if (this.radialChart) return true;

    return (this.dataValue?.datasets?.length || 0) > 1;
  }

  dataLabelValue(value) {
    if (typeof value === 'object' && value !== null) {
      return value.y ?? value.r ?? '';
    }

    const yFormat = this.detectYFormat();
    return this.formatValue(value, yFormat);
  }

  /**
   * Detect the Y-axis format from explicit config or by analyzing labels
   * @returns {string|null} The format type or null for default formatting
   */
  // The axis carrying numeric values: x for horizontal bars, y otherwise.
  get valueAxis() {
    return this.optionsValue?.indexAxis === 'y' ? 'x' : 'y';
  }

  get categoryAxis() {
    return this.valueAxis === 'y' ? 'x' : 'y';
  }

  detectYFormat() {
    const valueScale = this.optionsValue?.scales?.[this.valueAxis];

    // First check for explicit y_format from LLM
    const explicitFormat = valueScale?.format;
    if (explicitFormat) {
      return explicitFormat;
    }

    // Auto-detect based on the value-axis label
    const valueLabel = valueScale?.title?.text || '';
    if (this.isDurationLabel(valueLabel)) {
      return 'duration_seconds';
    }

    // Auto-detect based on dataset labels
    const datasets = this.dataValue?.datasets || [];
    for (const dataset of datasets) {
      if (this.isDurationLabel(dataset.label || '')) {
        return 'duration_seconds';
      }
    }

    return null;
  }

  /**
   * Check if a label indicates duration data
   * @param {string} label - The label to check
   * @returns {boolean}
   */
  isDurationLabel(label) {
    const lowerLabel = label.toLowerCase();
    const durationKeywords = [
      'duration',
      'sleep_duration',
      'time_in_bed',
      'time in bed',
      'sleep time',
      'rest duration',
      'hours slept',
      'seconds',
    ];
    return durationKeywords.some((keyword) => lowerLabel.includes(keyword));
  }

  /**
   * Get Y-axis tick configuration based on format type
   */
  getYAxisTicks(yFormat) {
    if (yFormat === 'duration_seconds') {
      return {
        callback: (seconds) => this.formatDurationSeconds(seconds),
      };
    } else if (yFormat === 'percentage') {
      return {
        callback: (value) => `${value}%`,
      };
    }
    return {};
  }

  /**
   * Format a value based on the y_format type
   */
  formatValue(value, yFormat) {
    if (yFormat === 'duration_seconds') {
      return this.formatDurationSeconds(value);
    } else if (yFormat === 'percentage') {
      return `${value}%`;
    }
    return value;
  }

  /**
   * Format seconds into human-readable duration (e.g., "7h 12m")
   */
  formatDurationSeconds(seconds) {
    if (seconds === null || seconds === undefined) return '';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const minutesText = t('js.charts.common.minutes', { minutes: mins });
    if (hours === 0) {
      return minutesText;
    }
    const hoursText = t('js.charts.common.hours', { hours });
    return mins > 0 ? `${hoursText} ${minutesText}` : hoursText;
  }

  deepMerge(target, source) {
    const result = { ...target };

    for (const key of Object.keys(source)) {
      if (
        source[key] &&
        typeof source[key] === 'object' &&
        !Array.isArray(source[key])
      ) {
        result[key] = this.deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }

    return result;
  }
}

// Make the controller Turbo-aware to handle caching properly
AiMessageChartController.prototype.connect = withPreviewPrevention(
  withDisconnectBeforeCache(AiMessageChartController.prototype.connect)
);

AiMessageChartController.prototype.disconnect = withPreviewPrevention(
  AiMessageChartController.prototype.disconnect
);

export default AiMessageChartController;
